const EXPORT_SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const EXPORT_TABLE_FONT_FAMILY = 'Inter, "Segoe UI", Arial, sans-serif';

const clampExportValue = (value, min, max) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return min;
  }

  return Math.min(Math.max(numericValue, min), max);
};

const createExportSvgElement = (tagName, attributes = {}) => {
  const element = document.createElementNS(EXPORT_SVG_NAMESPACE, tagName);

  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      element.setAttribute(name, String(value));
    }
  });

  return element;
};

const estimateExportTextWidth = (text, fontSize, fontWeight = 500) => {
  const normalizedText = String(text || '');
  const widthFactor = Number(fontWeight) >= 600 ? 0.62 : 0.56;
  return normalizedText.length * fontSize * widthFactor;
};

const truncateExportText = (text, maxChars) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) {
    return '';
  }

  if (!Number.isFinite(maxChars) || maxChars <= 1 || normalizedText.length <= maxChars) {
    return normalizedText;
  }

  return `${normalizedText.slice(0, Math.max(1, maxChars - 1)).trimEnd()}...`;
};

const wrapExportTableText = (text, maxWidth, fontSize, maxLines = 2) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) {
    return ['-'];
  }

  const safeMaxWidth = Math.max(48, Number(maxWidth) || 0);
  const estimatedCharWidth = Math.max(5.5, fontSize * 0.56);
  const maxCharsPerLine = Math.max(4, Math.floor(safeMaxWidth / estimatedCharWidth));
  const rawTokens = normalizedText.split(/\s+/).filter(Boolean);
  const tokens = rawTokens.flatMap((token) => {
    if (token.length <= maxCharsPerLine) {
      return [token];
    }

    const chunks = token.match(new RegExp(`.{1,${maxCharsPerLine}}`, 'g'));
    return chunks && chunks.length ? chunks : [token];
  });

  const lines = [];
  let currentLine = '';

  tokens.forEach((token) => {
    const candidate = currentLine ? `${currentLine} ${token}` : token;

    if (!currentLine || estimateExportTextWidth(candidate, fontSize) <= safeMaxWidth) {
      currentLine = candidate;
      return;
    }

    lines.push(currentLine);
    currentLine = token;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maxLines) {
    return lines;
  }

  const limitedLines = lines.slice(0, maxLines);
  limitedLines[maxLines - 1] = truncateExportText(limitedLines[maxLines - 1], maxCharsPerLine - 1);
  return limitedLines;
};

const createMultilineExportText = ({
  x,
  startY,
  lines,
  fontSize,
  lineHeight,
  fill = '#111827',
  fontWeight = 500,
  textAnchor = 'start',
}) => {
  const text = createExportSvgElement('text', {
    x: Number(x.toFixed(2)),
    y: Number(startY.toFixed(2)),
    fill,
    'font-size': fontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': fontWeight,
    'text-anchor': textAnchor,
  });

  lines.forEach((line, index) => {
    const tspan = createExportSvgElement('tspan', {
      x: Number(x.toFixed(2)),
      y: Number((startY + (index * lineHeight)).toFixed(2)),
    });
    tspan.textContent = line;
    text.appendChild(tspan);
  });

  return text;
};

export const appendConnectionsTableToExportSvg = (svgElement, {
  connections = [],
  selectedConnectionIndex = null,
  viewBox,
}) => {
  if (!(svgElement instanceof SVGElement) || !viewBox) {
    return;
  }

  const safeConnections = Array.isArray(connections) ? connections : [];
  const cardMaxWidth = Math.max(240, viewBox.width - 48);
  const cardWidth = Math.min(cardMaxWidth, 1120);
  const sectionGap = clampExportValue(viewBox.height * 0.07, 42, 88);
  const cardX = viewBox.minX + ((viewBox.width - cardWidth) / 2);
  const cardY = viewBox.minY + viewBox.height + sectionGap;
  const cardPaddingX = clampExportValue(cardWidth * 0.04, 18, 30);
  const cardPaddingY = clampExportValue(viewBox.height * 0.03, 18, 28);
  const titleFontSize = clampExportValue(cardWidth * 0.024, 18, 24);
  const subtitleFontSize = clampExportValue(titleFontSize - 6, 11, 14);
  const headerFontSize = clampExportValue(titleFontSize - 5, 12, 15);
  const bodyFontSize = clampExportValue(titleFontSize - 4, 13, 16);
  const bodyLineHeight = Math.round(bodyFontSize * 1.35);
  const titleBlockHeight = titleFontSize + subtitleFontSize + 14;
  const columnGap = clampExportValue(cardWidth * 0.04, 28, 44);
  const contentWidth = cardWidth - (cardPaddingX * 2);
  const columnWidth = Math.max(120, (contentWidth - columnGap) / 2);
  const headerRowHeight = Math.max(38, headerFontSize + 22);
  const headerRowY = cardY + cardPaddingY + titleBlockHeight + 12;
  const bodyStartY = headerRowY + headerRowHeight;

  const rowLayouts = safeConnections.map((connection, index) => {
    const sensorLines = wrapExportTableText(connection?.sensorPin, columnWidth - 10, bodyFontSize, 2);
    const microcontrollerLines = wrapExportTableText(connection?.microcontrollerPin, columnWidth - 10, bodyFontSize, 2);
    const maxLineCount = Math.max(sensorLines.length, microcontrollerLines.length);
    const rowHeight = Math.max(48, (maxLineCount * bodyLineHeight) + 22);

    return {
      index,
      sensorLines,
      microcontrollerLines,
      rowHeight,
      isSelected: selectedConnectionIndex === index,
    };
  });

  const rowsHeight = rowLayouts.length
    ? rowLayouts.reduce((total, row) => total + row.rowHeight, 0)
    : 64;
  const cardHeight = cardPaddingY + titleBlockHeight + 12 + headerRowHeight + rowsHeight + cardPaddingY;
  const leftColumnX = cardX + cardPaddingX;
  const rightColumnX = leftColumnX + columnWidth + columnGap;

  svgElement.style.setProperty('overflow', 'visible', 'important');

  const tableGroup = createExportSvgElement('g', {
    'data-export-connections-table': '1',
  });

  const cardBackground = createExportSvgElement('rect', {
    x: Number(cardX.toFixed(2)),
    y: Number(cardY.toFixed(2)),
    width: Number(cardWidth.toFixed(2)),
    height: Number(cardHeight.toFixed(2)),
    rx: 18,
    fill: '#ffffff',
    stroke: '#e5e7eb',
    'stroke-width': 1.5,
  });
  tableGroup.appendChild(cardBackground);

  const titleY = cardY + cardPaddingY + titleFontSize;
  const title = createExportSvgElement('text', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number(titleY.toFixed(2)),
    fill: '#111827',
    'font-size': titleFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 700,
  });
  title.textContent = 'Pin Connections';
  tableGroup.appendChild(title);

  const subtitle = createExportSvgElement('text', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number((titleY + subtitleFontSize + 10).toFixed(2)),
    fill: '#6b7280',
    'font-size': subtitleFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 500,
  });
  subtitle.textContent = rowLayouts.length
    ? `${rowLayouts.length} saved connection${rowLayouts.length === 1 ? '' : 's'}`
    : 'No saved connections';
  tableGroup.appendChild(subtitle);

  const headerBackground = createExportSvgElement('rect', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number(headerRowY.toFixed(2)),
    width: Number(contentWidth.toFixed(2)),
    height: Number(headerRowHeight.toFixed(2)),
    rx: 10,
    fill: '#f8fafc',
  });
  tableGroup.appendChild(headerBackground);

  const headerBaselineY = headerRowY + (headerRowHeight / 2) + (headerFontSize * 0.35);
  tableGroup.appendChild(createExportSvgElement('text', {
    x: Number(leftColumnX.toFixed(2)),
    y: Number(headerBaselineY.toFixed(2)),
    fill: '#475569',
    'font-size': headerFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 700,
  }));
  tableGroup.lastChild.textContent = 'Sensor Board Pin';

  tableGroup.appendChild(createExportSvgElement('text', {
    x: Number(rightColumnX.toFixed(2)),
    y: Number(headerBaselineY.toFixed(2)),
    fill: '#475569',
    'font-size': headerFontSize,
    'font-family': EXPORT_TABLE_FONT_FAMILY,
    'font-weight': 700,
  }));
  tableGroup.lastChild.textContent = 'Microcontroller Pin';

  if (!rowLayouts.length) {
    const emptyState = createExportSvgElement('text', {
      x: Number((cardX + (cardWidth / 2)).toFixed(2)),
      y: Number((bodyStartY + 38).toFixed(2)),
      fill: '#6b7280',
      'font-size': bodyFontSize,
      'font-family': EXPORT_TABLE_FONT_FAMILY,
      'font-weight': 500,
      'text-anchor': 'middle',
    });
    emptyState.textContent = 'No pin connections available.';
    tableGroup.appendChild(emptyState);
    svgElement.appendChild(tableGroup);
    return;
  }

  let currentRowY = bodyStartY;

  rowLayouts.forEach((row, rowIndex) => {
    const rowFill = row.isSelected
      ? '#fff7ed'
      : rowIndex % 2 === 0
        ? '#ffffff'
        : '#fcfdff';
    const rowBackground = createExportSvgElement('rect', {
      x: Number(leftColumnX.toFixed(2)),
      y: Number(currentRowY.toFixed(2)),
      width: Number(contentWidth.toFixed(2)),
      height: Number(row.rowHeight.toFixed(2)),
      rx: 10,
      fill: rowFill,
    });
    tableGroup.appendChild(rowBackground);

    const sensorTextHeight = ((row.sensorLines.length - 1) * bodyLineHeight) + bodyFontSize;
    const microTextHeight = ((row.microcontrollerLines.length - 1) * bodyLineHeight) + bodyFontSize;
    const sensorStartY = currentRowY + ((row.rowHeight - sensorTextHeight) / 2) + (bodyFontSize * 0.82);
    const microStartY = currentRowY + ((row.rowHeight - microTextHeight) / 2) + (bodyFontSize * 0.82);

    tableGroup.appendChild(createMultilineExportText({
      x: leftColumnX,
      startY: sensorStartY,
      lines: row.sensorLines,
      fontSize: bodyFontSize,
      lineHeight: bodyLineHeight,
      fill: '#111827',
      fontWeight: row.isSelected ? 700 : 600,
    }));

    tableGroup.appendChild(createMultilineExportText({
      x: rightColumnX,
      startY: microStartY,
      lines: row.microcontrollerLines,
      fontSize: bodyFontSize,
      lineHeight: bodyLineHeight,
      fill: '#111827',
      fontWeight: row.isSelected ? 700 : 600,
    }));

    currentRowY += row.rowHeight;
  });

  svgElement.appendChild(tableGroup);
};
